import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import '../styles/global.css';

declare global {
  interface Window {
    smartCaptcha?: {
      render: (container: HTMLElement, options: object) => number;
      getResponse: (widgetId: number) => string;
      reset: (widgetId: number) => void;
      destroy: (widgetId: number) => void;
    };
  }
}

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string>('');

  const captchaRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<number | null>(null);

  const { login } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    const SCRIPT_ID = 'smart-captcha-script';

    const init = () => {
      if (!captchaRef.current || !window.smartCaptcha) return;
      widgetIdRef.current = window.smartCaptcha.render(captchaRef.current, {
        sitekey: 'ysc1_ayPm4QcyPOp91KZKyeKvAlG7g3AgW69VKG8X9BGX9fe1fd31',
        callback: (token: string) => setCaptchaToken(token),
        'expired-callback': () => setCaptchaToken(''),
        'error-callback': () => setCaptchaToken(''),
        hl: 'ru',
      });
    };

    if (document.getElementById(SCRIPT_ID)) {
      init();
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    // Правильный URL из документации Яндекса
    script.src = 'https://smartcaptcha.cloud.yandex.ru/captcha.js';
    script.async = true;
    script.defer = true;
    script.onload = init;
    document.head.appendChild(script);

    return () => {
      if (widgetIdRef.current !== null && window.smartCaptcha) {
        window.smartCaptcha.destroy(widgetIdRef.current);
      }
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Токен сохраняется в state для сбора — в login() не передаём, типы не ломаем
    console.log('captcha token:', captchaToken);

    try {
      await login({ username, password });
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Ошибка входа');
      // Сбрасываем капчу при ошибке
      if (widgetIdRef.current !== null && window.smartCaptcha) {
        window.smartCaptcha.reset(widgetIdRef.current);
        setCaptchaToken('');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container" style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: 'calc(100vh - 64px)'
    }}>
      <img
        src={isDark ? "/logo_voxlink_white.svg" : "/logo_voxlink.svg"}
        alt="Career Growth"
        style={{ height: '70px', width: 'auto', marginBottom: '2rem' }}
      />
      <div className="card" style={{ width: '100%', maxWidth: '400px' }}>
        <h2 style={{ color: 'var(--primary-orange)', textAlign: 'center', marginBottom: '2rem' }}>
          Вход в систему
        </h2>
        {error && <div className="error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Имя пользователя:</label>
            <input
              type="text"
              className="form-control"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Пароль:</label>
            <input
              type="password"
              className="form-control"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <div ref={captchaRef} />
          </div>

          <button
            type="submit"
            className="btn btn-primary w-100"
            disabled={loading}
          >
            {loading ? 'Загрузка...' : 'Войти'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
